<script>
  import { Icon, ChevronRight, ChevronDown, CursorArrowRays } from "svelte-hero-icons";
  import { execution, jobs } from "../store";
  import Toolbar from "./Toolbar.svelte";
  import ImportableImage from "$lib/Elements/ImportableImage.svelte";
  import { imageManager } from "$lib/shared_stores";
  import { persisted } from "$lib/utils/persisted-stores";
  import { isImage, isImageUrl } from "$lib/utils/image";
  import { filterEvents } from "./eventFilter";
  import { tooltip } from "$utils/tooltip";
  import LuaError from "./LuaError.svelte";

  let expandedEvents = {};
  let events = execution.eventLog;
  let persistLogs = false;

  const eventFilterMode = persisted("event_filter_mode", "logs_only");

  $: execution.setPersistLogs(persistLogs);

  $: filteredEvents = filterEvents($events, $eventFilterMode);

  function goToStep(stepId) {
    execution.setCurrentStep(stepId);
    execution.setActiveTab("tree");
  }

  function formatTime(event) {
    if (event.inserted_at) {
      try {
        const d = new Date(event.inserted_at);
        return d.toLocaleTimeString([], {
          hour12: true,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      } catch {
        // invalid date, fall through to seq fallback
      }
    }
    return event.seq ? `#${event.seq}` : "";
  }

  function initializeExpandedEvents() {
    expandedEvents = $events.reduce((acc, event) => {
      acc[event.id] = false;
      return acc;
    }, {});
  }

  initializeExpandedEvents();

  function toggleEvent(id) {
    expandedEvents[id] = !expandedEvents[id];
    expandedEvents = expandedEvents;
  }

  function clearLogs() {
    execution.clearLogs();
    expandedEvents = {};
  }

  function expandAll() {
    expandedEvents = $events.reduce((acc, event) => {
      acc[event.id] = true;
      return acc;
    }, {});
  }

  function collapseAll() {
    expandedEvents = $events.reduce((acc, event) => {
      acc[event.id] = false;
      return acc;
    }, {});
  }

  // See: event_components.ex
  function getEventTypeColor(type) {
    switch (type) {
      case "agent_capture":
      case "agent_reasoning":
      case "agent_tool_calls":
        return "text-cyan-400";
      case "asset_imported":
      case "asset_uploaded":
      case "har_uploaded":
      case "ui_detect_result":
      case "ui_screen_changed":
      case "visual_comparison":
        return "text-yellow-500";
      case "automation_started":
      case "automation_ended":
        return "text-blue-500";
      case "error":
      case "workflow_fail_step":
      case "workflow_log_error":
        return "text-red-500";
      case "log":
      case "log_image":
      case "workflow":
        return "text-content-subtle";
      case "metric":
      case "network_metrics":
        return "text-purple-500";
      case "test":
      case "test_cases":
        return "text-green-500";
      default:
        return "text-white";
    }
  }

  function getRelevantInfo(event) {
    if (event.type === "workflow") {
      const { step_id, type } = event.data;
      return `${type} - ${step_id}`;
    }
    if (event.type === "agent_capture") {
      return `agent_capture (iter ${event.data.iteration})`;
    }
    if (event.type === "agent_reasoning") {
      const truncated = event.data.reasoning?.substring(0, 50) || "";
      return `agent_reasoning (iter ${event.data.iteration}): ${truncated}...`;
    }
    if (event.type === "agent_tool_calls") {
      const tools = event.data.tool_calls?.map((tc) => tc.tool).join(", ") || "";
      return `agent_tool_calls (iter ${event.data.iteration}): ${tools}`;
    }
    return event.type;
  }

  function formatValue(value) {
    // If value is an object and might contain image data
    if (value && typeof value === "object") {
      const processed = {};
      for (const [key, prop] of Object.entries(value)) {
        processed[key] =
          typeof prop === "object" && "image" in prop && prop.image.startsWith("data:")
            ? "<truncated image>"
            : prop;
      }
      return JSON.stringify(processed, null, 2);
    }

    if (typeof value === "string") {
      return value;
    }

    return JSON.stringify(value);
  }
</script>

<Toolbar
  onExpandAll={expandAll}
  onCollapseAll={collapseAll}
  onClearLogs={clearLogs}
  bind:persistLogs
  {eventFilterMode}
/>

<div class="bg-gray-900 text-white p-2 font-mono text-xs rounded-md overflow-auto">
  {#if filteredEvents.length === 0}
    <div class="flex items-center justify-center h-full text-content-muted p-4 text-md text-center">
      No events yet. Start a Trace to see some events.
    </div>
  {:else}
    {#each filteredEvents as event, index}
      <div class="border-gray-800 py-1" class:border-b={index !== filteredEvents.length - 1}>
        {#if $eventFilterMode === "logs_only"}
          <div class="flex items-start group">
            <span class="text-content-muted shrink-0 tabular-nums">{formatTime(event)}</span>
            <span class="ml-2 font-bold {getEventTypeColor(event.type)} shrink-0"
              >[{event.type}]</span
            >
            {#if event.data.structured_error}
              <div class="ml-2 min-w-0 flex-1">
                <LuaError compact error={event.data.structured_error} />
              </div>
            {:else}
              <span class="ml-2 text-gray-300 whitespace-pre-wrap break-all"
                >{event.data.message || event.data.reason || formatValue(event.data)}</span
              >
            {/if}
            {#if event._stepId}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <span
                use:tooltip
                aria-label="Go to block"
                class="ml-auto pl-2 shrink-0 opacity-0 group-hover:opacity-100 cursor-pointer text-indigo-400 hover:text-indigo-300 transition-opacity"
                on:click|stopPropagation={() => goToStep(event._stepId)}
              >
                <Icon src={CursorArrowRays} class="w-3.5 h-3.5 inline" />
              </span>
            {/if}
          </div>
        {:else}
          <!-- svelte-ignore a11y-click-events-have-key-events -->
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div class="cursor-pointer flex items-center" on:click={() => toggleEvent(event.id)}>
            <Icon
              src={expandedEvents[event.id] ? ChevronDown : ChevronRight}
              class="w-3 h-3 text-content-subtle mr-1 shrink-0"
            />
            <span class="font-bold {getEventTypeColor(event.type)} truncate">
              {getRelevantInfo(event)}
            </span>
            <span class="ml-auto pl-2 text-content-muted tabular-nums shrink-0"
              >#{event.seq || "N/A"}</span
            >
          </div>
          {#if expandedEvents[event.id]}
            <div class="mt-1 ml-4">
              {#each Object.entries(event.data) as [key, value]}
                <div class="text-xs">
                  <span class="text-purple-400">{key}:</span>
                  {#if isImage(value)}
                    <div class="mt-2 mb-4">
                      <ImportableImage
                        src={imageManager.src(value.image || value)}
                        alt={key}
                        sessionId={$jobs.m3u8}
                      />
                    </div>
                  {:else if isImageUrl(value)}
                    <a target="_blank" rel="noopener noreferrer" href={value}>
                      <span class="text-gray-300 break-all">{value}</span>
                      <div class="mt-2 mb-4">
                        <ImportableImage src={value} alt={key} sessionId={$jobs.m3u8} />
                      </div>
                    </a>
                  {:else}
                    <span class="text-gray-300 whitespace-pre-wrap">{formatValue(value)}</span>
                  {/if}
                </div>
              {/each}
            </div>
          {/if}
        {/if}
      </div>
    {/each}
  {/if}
</div>
